import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  type ReferralFilters,
  type ReferralRow,
} from "../../../domain/entities/referral-backoffice";
import { type ReferralsBackofficeDispatch } from "../../config";
import { type ExtraArgument } from "../../extra-argument";
import { type State } from "../../state";
import { thunkTypes } from "../thunk-types";

/** The API's ceiling (`page_size` validates `max=100`), so the fewest calls. */
const EXPORT_PAGE_SIZE = 100;

/**
 * A ceiling on the whole export. Without one a backend that kept handing back
 * the same `next_token` would loop forever while the operator watches a
 * spinner; 20 000 rows is far past any real period and cheap to bound.
 */
export const EXPORT_MAX_ROWS = 20_000;

/**
 * Every referral matching the filters, paged through to the end.
 *
 * The export cannot reuse what the screen has: the table is keyset-paginated,
 * so it holds only the pages the operator happened to scroll through, and a CSV
 * built from that silently omits the rest. This reads its own pages and returns
 * them without touching the displayed list.
 */
export const fetchAllReferrals = createAsyncThunk<
  ReferralRow[],
  ReferralFilters,
  { dispatch: ReferralsBackofficeDispatch; extra: ExtraArgument; state: State }
>(thunkTypes.FETCH_ALL_REFERRALS, async (filters, { extra }) => {
  const all: ReferralRow[] = [];
  let pageToken: string | undefined;

  do {
    const page = await extra.referralsBackofficeRepo.list({
      ...filters,
      pageSize: EXPORT_PAGE_SIZE,
      pageToken,
    });
    all.push(...page.items);
    pageToken = page.items.length > 0 ? page.nextToken : undefined;
  } while (pageToken && all.length < EXPORT_MAX_ROWS);

  return all;
});
